document.documentElement.classList.add('lussera-js');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const initializedFaqs = new WeakSet();
const observedReveals = new WeakSet();

/** @type {IntersectionObserver | null} */
let revealObserver = null;

function safeSessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (_error) {
    // Storage can be unavailable in private/restricted browser contexts.
  }
}

function dismissIntro(intro, { immediate = false } = {}) {
  if (!(intro instanceof HTMLElement)) {
    document.body.classList.remove('lussera-intro-active');
    return;
  }

  document.body.classList.remove('lussera-intro-active');

  if (immediate || reduceMotion) {
    intro.remove();
    return;
  }

  intro.classList.add('is-leaving');
  window.setTimeout(() => intro.remove(), 650);
}

function initIntro(root = document) {
  const intro = root.querySelector?.('[data-lussera-intro]');
  if (!(intro instanceof HTMLElement) || intro.dataset.initialized === 'true') return;

  intro.dataset.initialized = 'true';
  const seenIntro = safeSessionGet('lussera-intro-seen') === 'true';
  const delay = seenIntro || reduceMotion ? 80 : 1050;

  document.body.classList.add('lussera-intro-active');

  const timer = window.setTimeout(() => {
    dismissIntro(intro);
    safeSessionSet('lussera-intro-seen', 'true');
  }, delay);

  // Never let a loading animation keep the storefront locked after an error,
  // tab restore, or browser timer suspension.
  window.setTimeout(() => {
    window.clearTimeout(timer);
    if (document.contains(intro)) dismissIntro(intro, { immediate: true });
  }, 2600);
}

function getRevealObserver() {
  if (revealObserver || !('IntersectionObserver' in window) || reduceMotion) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver?.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  return revealObserver;
}

function initReveals(root = document) {
  const reveals = root.querySelectorAll?.('.lussera-reveal') ?? [];
  const observer = getRevealObserver();

  reveals.forEach((element) => {
    if (observedReveals.has(element)) return;
    observedReveals.add(element);

    if (!observer || reduceMotion) {
      element.classList.add('is-visible');
      return;
    }

    observer.observe(element);
  });

  // Content must remain usable even if a browser/WebView fails to dispatch
  // IntersectionObserver callbacks after a cached-page restore.
  window.setTimeout(() => {
    reveals.forEach((element) => {
      element.classList.add('is-visible');
      observer?.unobserve(element);
    });
  }, 2400);
}

function initFaqs(root = document) {
  const faqContainers = root.querySelectorAll?.('.lussera-faq') ?? [];

  faqContainers.forEach((container) => {
    if (initializedFaqs.has(container)) return;
    initializedFaqs.add(container);

    container.querySelectorAll('details').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        container.querySelectorAll('details[open]').forEach((openDetail) => {
          if (openDetail !== detail) openDetail.removeAttribute('open');
        });
      });
    });
  });
}

function initLusseraUi(root = document) {
  initIntro(root);
  initReveals(root);
  initFaqs(root);
}

function cleanupStaleLocks() {
  const openMenu = document.querySelector('#Details-menu-drawer-container[open]');
  if (openMenu) return;

  document.documentElement.removeAttribute('data-menu-drawer-open');
  document.body.classList.remove('menu-drawer-scroll-locked', 'lussera-intro-active');
  document.documentElement.style.removeProperty('--menu-drawer-scroll-y');
}

function ready() {
  initLusseraUi(document);
  cleanupStaleLocks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ready, { once: true });
} else {
  ready();
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    cleanupStaleLocks();
    document.querySelectorAll('.lussera-reveal').forEach((element) => element.classList.add('is-visible'));
    const intro = document.querySelector('[data-lussera-intro]');
    if (intro instanceof HTMLElement) dismissIntro(intro, { immediate: true });
  }
});

// Shopify's theme editor can replace sections without a full page load.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initLusseraUi(event.target);
});


function addCalendarDays(startDate, days) {
  const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  date.setDate(date.getDate() + days);
  return date;
}

function formatDeliveryRange(startDate, endDate) {
  const dayFormatter = new Intl.DateTimeFormat('ro-RO', { day: 'numeric' });
  const monthFormatter = new Intl.DateTimeFormat('ro-RO', { month: 'long' });
  const dayMonthFormatter = new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long' });
  const fullFormatter = new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });

  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  if (sameMonth) {
    return `${dayFormatter.format(startDate)}–${dayMonthFormatter.format(endDate)}`;
  }

  if (sameYear) {
    return `${dayMonthFormatter.format(startDate)} – ${dayMonthFormatter.format(endDate)}`;
  }

  return `${fullFormatter.format(startDate)} – ${fullFormatter.format(endDate)}`;
}

function initDeliveryWindows(root = document) {
  const outputs = root.querySelectorAll?.('[data-lussera-delivery-window]') ?? [];
  if (!outputs.length) return;

  const now = new Date();
  const earliest = addCalendarDays(now, 5);
  const latest = addCalendarDays(now, 7);
  const range = formatDeliveryRange(earliest, latest);
  outputs.forEach((output) => {
    const shortLabel = output.hasAttribute('data-lussera-delivery-short');
    output.textContent = shortLabel ? `Livrare ${range}` : `Ajunge între ${range}`;
  });
}


function formatIsoLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseIsoLocalDate(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function initDeliveryDeadlineCalendars(root = document) {
  const inputs = root.querySelectorAll?.('[data-lussera-deadline-input]') ?? [];
  if (!inputs.length) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  inputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    input.min = formatIsoLocalDate(today);

    const wrapper = input.closest('[data-lussera-delivery-deadline]');
    const help = wrapper?.querySelector('[data-lussera-deadline-help]');
    if (!(help instanceof HTMLElement)) return;

    const updateHelp = () => {
      const selected = parseIsoLocalDate(input.value);
      if (!selected) {
        help.textContent = 'Livrarea standard durează aproximativ 5–7 zile. Dacă alegi o dată, aceasta va apărea în comandă.';
        wrapper?.removeAttribute('data-urgent-date');
        return;
      }

      const differenceMs = selected.getTime() - today.getTime();
      const daysAway = Math.round(differenceMs / 86400000);
      if (daysAway < 5) {
        help.textContent = 'Data este mai apropiată decât intervalul standard de 5–7 zile. Te rugăm să ne contactezi pentru confirmare.';
        wrapper?.setAttribute('data-urgent-date', 'true');
      } else {
        help.textContent = 'Data limită a fost selectată și va apărea în comandă.';
        wrapper?.removeAttribute('data-urgent-date');
      }
    };

    if (input.dataset.lusseraDeadlineReady !== 'true') {
      input.dataset.lusseraDeadlineReady = 'true';
      input.addEventListener('change', updateHelp);
      input.addEventListener('input', updateHelp);
    }
    updateHelp();
  });
}


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { initDeliveryWindows(document); initDeliveryDeadlineCalendars(document); }, { once: true });
} else {
  initDeliveryWindows(document);
  initDeliveryDeadlineCalendars(document);
}

document.addEventListener('shopify:section:load', (event) => { initDeliveryWindows(event.target); initDeliveryDeadlineCalendars(event.target); });

// Cart drawers and quick-add areas can be replaced without a Shopify section-load event.
// Recalculate the visible date whenever new delivery labels are inserted into the DOM.
if ('MutationObserver' in window) {
  const deliveryObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('[data-lussera-delivery-window], [data-lussera-delivery-deadline]') || node.querySelector?.('[data-lussera-delivery-window], [data-lussera-delivery-deadline]')) {
          initDeliveryWindows(node.matches?.('[data-lussera-delivery-window]') ? node.parentElement || node : node);
          initDeliveryDeadlineCalendars(node);
          return;
        }
      }
    }
  });
  const startDeliveryObserver = () => {
    if (document.body) deliveryObserver.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startDeliveryObserver, { once: true });
  else startDeliveryObserver();
}
